import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { defaultFonts, formatDateRange, typography } from "@event-planner/shared";

interface EventCardPreviewProps {
  name: string;
  startDate: string;
  endDate: string;
  venue: string;
  city: string;
  heroImageUrl: string | null;
  /** Hex color for the gradient scrim. Defaults to "#000000". */
  overlayColor?: string;
  /** Opacity of the gradient scrim (0–1). Defaults to 0.3. */
  overlayOpacity?: number;
  /** CSS gradient direction. Defaults to "to top". */
  overlayDirection?: string;
}

/**
 * Live preview of the mobile event card. Mimics the exact layout attendees
 * see in the Events list on the mobile app: hero image with the event title
 * overlaid at the bottom, dates and venue below. Updates in real-time as the
 * organizer edits the form.
 */
export function EventCardPreview({
  name,
  startDate,
  endDate,
  venue,
  city,
  heroImageUrl,
  overlayColor = "#050024",
  overlayOpacity = 0.7,
  overlayDirection = "to bottom",
}: EventCardPreviewProps) {
  const displayName = name || "Event Name";
  const dateRange = formatDateRange(startDate, endDate);
  const location = [venue, city].filter(Boolean).join(", ");

  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Mobile App Preview
      </Typography>
      <Paper
        elevation={3}
        sx={{
          width: 280,
          borderRadius: 3,
          overflow: "hidden",
          position: "sticky",
          top: 16,
        }}
      >
        {/* Hero area with overlaid title */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 160,
            bgcolor: heroImageUrl ? "transparent" : "primary.main",
          }}
        >
          {heroImageUrl && (
            <Box
              component="img"
              src={heroImageUrl}
              alt=""
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}
          {/* Gradient scrim for text legibility */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(${overlayDirection}, ${overlayColor}${Math.round(overlayOpacity * 255).toString(16).padStart(2, "0")} 0%, transparent 100%)`,
            }}
          />
          {/* Title */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
            }}
          >
            <Typography
              sx={{
                color: "#FFFFFF",
                fontFamily: defaultFonts.admin.display,
                fontSize: typography.headlineLarge.fontSize,
                lineHeight: `${typography.headlineLarge.lineHeight}px`,
                fontWeight: typography.headlineLarge.fontWeight,
                letterSpacing: typography.headlineLarge.letterSpacing,
                textShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
            >
              {displayName}
            </Typography>
          </Box>
        </Box>

        {/* Dates and venue below the hero */}
        <Box sx={{ px: 2, py: 1.5 }}>
          {dateRange && (
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {dateRange}
            </Typography>
          )}
          {location && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {location}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
